import moment from 'moment';

export function formatCapTableDate(value) {
  if (!value) return null;
  if (typeof value.format === 'function') {
    return value.format('YYYY-MM-DD');
  }
  const parsed = moment(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : null;
}

/**
 * Preferred / SAFE rows must have unique Date of transaction values when both
 * class name and date are filled. Dividend start date duplicates are allowed.
 */
export function findDuplicateTransactionDates(tableData, classFieldName) {
  const errorIndexes = [];
  const dateToIndexes = {};

  tableData.forEach((row, index) => {
    const className = row[classFieldName]?.value;
    const dateValue = row.transactionDate?.value;
    if (!className || !dateValue) return;

    const dateKey = formatCapTableDate(dateValue);
    if (!dateKey) return;

    if (!dateToIndexes[dateKey]) dateToIndexes[dateKey] = [];
    dateToIndexes[dateKey].push(index);
  });

  Object.values(dateToIndexes).forEach((indexes) => {
    if (indexes.length >= 2) {
      indexes.forEach((index) => {
        if (!errorIndexes.includes(index)) errorIndexes.push(index);
      });
    }
  });

  return {
    hasDuplicate: errorIndexes.length >= 2,
    errorIndexes,
  };
}

/**
 * Validates saved cap structure objects (userData.capData format).
 * Dividend start date duplicates are allowed.
 */
export function findDuplicateTransactionDatesInCapSection(sectionData) {
  if (!sectionData || typeof sectionData !== 'object') {
    return { hasDuplicate: false, duplicateClassNames: [] };
  }

  const dateToClassNames = {};
  const duplicateClassNames = [];

  Object.entries(sectionData).forEach(([className, classData]) => {
    if (!className || !classData?.transactionDate) return;

    const dateKey = formatCapTableDate(classData.transactionDate);
    if (!dateKey) return;

    if (!dateToClassNames[dateKey]) dateToClassNames[dateKey] = [];
    dateToClassNames[dateKey].push(className);
  });

  Object.values(dateToClassNames).forEach((classNames) => {
    if (classNames.length >= 2) {
      classNames.forEach((name) => {
        if (!duplicateClassNames.includes(name)) duplicateClassNames.push(name);
      });
    }
  });

  return {
    hasDuplicate: duplicateClassNames.length >= 2,
    duplicateClassNames,
  };
}

export function getCapStructureTransactionDateErrors(capData) {
  const preferred = findDuplicateTransactionDatesInCapSection(capData?.preferred);
  const safeConvertible = findDuplicateTransactionDatesInCapSection(capData?.safeConvertible);

  let errorMessage = '';
  if (preferred.hasDuplicate) {
    errorMessage = 'Duplicate Date of transaction. Each class of stock must have a unique transaction date.';
  } else if (safeConvertible.hasDuplicate) {
    errorMessage = 'Duplicate Date of transaction. Each SAFE/Convertible Note must have a unique transaction date.';
  }

  return {
    hasError: preferred.hasDuplicate || safeConvertible.hasDuplicate,
    errorMessage,
    preferred,
    safeConvertible,
  };
}

const EMPTY_CAP_VALUES = [null, undefined, '', '0', 'N/A', 'NA', '-'];

function isEmptyCapValue(value) {
  if (value === 0 || value === '0') return true;
  return EMPTY_CAP_VALUES.includes(value);
}

function collectWarrantSectionErrors(sectionData) {
  const missingByClass = {};
  if (!sectionData || typeof sectionData !== 'object') {
    return { hasError: false, missingByClass, messages: [] };
  }

  Object.entries(sectionData).forEach(([className, classData]) => {
    if (!classData || typeof classData !== 'object') return;
    const missing = [];
    if (isEmptyCapValue(classData.type)) missing.push('type');
    if (isEmptyCapValue(classData.quantity)) missing.push('outstanding');
    if (isEmptyCapValue(classData.strikePrice)) missing.push('exercise price');
    if (
      String(classData.type || '').toLowerCase() === 'preferred' &&
      isEmptyCapValue(classData.preferredClass)
    ) {
      missing.push('class of stock');
    }
    if (missing.length) {
      missingByClass[className || 'Warrant'] = missing;
    }
  });

  const messages = Object.entries(missingByClass).map(([className, missing]) => (
    `${className}: select/enter ${missing.join(', ')}`
  ));

  return {
    hasError: messages.length > 0,
    missingByClass,
    messages,
  };
}

/**
 * Validates saved warrant rows in capData before Run calc engine.
 */
export function getCapStructureWarrantErrors(capData) {
  const warrants = collectWarrantSectionErrors(capData?.warrant);
  return {
    hasError: warrants.hasError,
    errorMessage: warrants.hasError
      ? `Warrant is missing required field(s). ${warrants.messages.join('; ')}.`
      : '',
    warrants,
  };
}

export function findDuplicateClassNames(tableData, classFieldName) {
  const errorIndexes = [];
  const classToIndexes = {};

  tableData.forEach((row, index) => {
    const className = row[classFieldName]?.value;
    if (!className) return;

    if (!classToIndexes[className]) classToIndexes[className] = [];
    classToIndexes[className].push(index);
  });

  Object.values(classToIndexes).forEach((indexes) => {
    if (indexes.length >= 2) {
      indexes.forEach((index) => {
        if (!errorIndexes.includes(index)) errorIndexes.push(index);
      });
    }
  });

  return {
    hasDuplicate: errorIndexes.length >= 2,
    errorIndexes,
  };
}

export function findDuplicateClassAndTransactionDate(tableData, classFieldName) {
  const errorIndexes = [];

  tableData.forEach((row1, i) => {
    tableData.forEach((row2, j) => {
      if (i >= j) return;

      const class1 = row1[classFieldName]?.value;
      const class2 = row2[classFieldName]?.value;
      const date1 = formatCapTableDate(row1.transactionDate?.value);
      const date2 = formatCapTableDate(row2.transactionDate?.value);

      if (class1 && class2 && class1 === class2 && date1 && date2 && date1 === date2) {
        if (!errorIndexes.includes(i)) errorIndexes.push(i);
        if (!errorIndexes.includes(j)) errorIndexes.push(j);
      }
    });
  });

  return {
    hasDuplicate: errorIndexes.length >= 2,
    errorIndexes,
  };
}
